/**
 * Centralized date utilities for timezone-safe date handling
 *
 * THE PROBLEM:
 * JavaScript's `new Date('YYYY-MM-DD')` interprets date-only strings as UTC midnight.
 * When `toLocaleDateString()` is called, it converts to local time.
 * For users in timezones west of UTC (e.g., US Pacific UTC-8), this shows
 * the wrong day: "2026-02-20" at UTC midnight becomes "Feb 19" at 4pm local.
 *
 * THE SOLUTION:
 * These utilities parse date-only strings as LOCAL midnight, ensuring the
 * displayed date matches the database date regardless of user timezone.
 *
 * @module date-utils
 */

/**
 * Parse a date string as LOCAL time (not UTC).
 *
 * This function safely handles both:
 * - Date-only strings ('2026-02-20') - parsed as local midnight
 * - ISO datetime strings ('2026-02-20T18:00:00Z') - parsed normally
 *
 * @param dateStr - Date string from database
 * @returns Date object in local time
 *
 * @example
 * // Date-only string: parsed as local midnight
 * parseLocalDate('2026-02-20') // Feb 20, 2026 00:00:00 local time
 *
 * // ISO datetime: parsed as specified timezone
 * parseLocalDate('2026-02-20T18:00:00Z') // Feb 20, 2026 (converted to local)
 */
export function parseLocalDate(
  dateStr: string | Date | null | undefined,
): Date | null {
  if (!dateStr) return null;

  // Already a Date object
  if (dateStr instanceof Date) {
    return dateStr;
  }

  // ISO datetime with time component - parse normally
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  // Date-only string (YYYY-MM-DD) - append T00:00:00 to force local interpretation
  // Without the time component, JS would interpret as UTC midnight
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

// Lives in @kit/shared so feature packages use the same local-date rule.
export { localTodayISODate } from '@kit/shared/date-utils';

/**
 * Format a date for display with timezone safety.
 *
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier (default: en-US)
 * @returns Formatted date string or '-' if date is null
 *
 * @example
 * formatLocalDate('2026-02-20') // "February 20, 2026"
 * formatLocalDate('2026-02-20', { weekday: 'short' }) // "Thu, February 20, 2026"
 */
export function formatLocalDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  return parsed.toLocaleDateString(locale, options);
}

/**
 * Format date with long format (January 15, 2026)
 */
export function formatDateLong(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'long', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with short format (Jan 15, 2026)
 */
export function formatDateShort(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with weekday (Fri, Jan 15, 2026)
 */
export function formatDateWithWeekday(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with full weekday (Friday, January 15, 2026)
 */
export function formatDateWithFullWeekday(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    locale,
  );
}

/**
 * Format date in EU style (15 Jan 2026)
 */
export function formatDateEU(
  date: string | Date | null | undefined,
  locale: string = 'en-GB',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date for Fever API (4-nov-2025)
 */
export function formatDateFever(
  date: string | Date | null | undefined,
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  const day = parsed.getDate();
  const month = parsed
    .toLocaleDateString('en-GB', { month: 'short' })
    .toLowerCase();
  const year = parsed.getFullYear();

  return `${day}-${month}-${year}`;
}

/**
 * Format date range (Jan 15 - Jan 20, 2026)
 */
export function formatDateRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  if (!start) return '-';
  if (!end) return formatDateShort(startDate, locale);

  // Same day
  if (start.toDateString() === end.toDateString()) {
    return formatDateShort(startDate, locale);
  }

  // Same year - don't repeat year
  if (start.getFullYear() === end.getFullYear()) {
    const startStr = start.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    });
    const endStr = end.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startStr} - ${endStr}`;
  }

  // Different years
  return `${formatDateShort(startDate, locale)} - ${formatDateShort(endDate, locale)}`;
}

/**
 * Get relative date description (Today, Tomorrow, Yesterday, or formatted date)
 */
export function getRelativeDateLabel(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(parsed);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';

  return formatDateWithWeekday(date, locale);
}

/**
 * Check if a date is today
 */
export function isToday(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  return (
    parsed.getDate() === today.getDate() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getFullYear() === today.getFullYear()
  );
}

/**
 * Check if a date is in the past
 */
export function isPast(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parsed < today;
}

/**
 * Check if a date is in the future
 */
export function isFuture(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return parsed > today;
}

/**
 * Format a timestamp (datetime) for display.
 * Unlike formatLocalDate, this preserves the time component for proper timezone conversion.
 *
 * @param timestamp - ISO datetime string (e.g., '2026-02-20T18:00:00Z')
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier
 * @returns Formatted datetime string
 */
export function formatTimestamp(
  timestamp: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  locale: string = 'en-US',
): string {
  if (!timestamp) return '-';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString(locale, options);
}

/**
 * Format date portion of a timestamp (datetime) for display.
 * This converts the timestamp to local time and formats only the date.
 *
 * Use this when you have a full timestamp but only want to display the date.
 *
 * @param timestamp - ISO datetime string (e.g., '2026-02-20T18:00:00Z')
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier
 * @returns Formatted date string
 */
export function formatTimestampDate(
  timestamp: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  if (!timestamp) return '-';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, options);
}

/**
 * The timezone a person's age is computed in. Ballee is operated from
 * Switzerland, and the database age gate (BAL-900, migration
 * 20261103010000_profile_publish_age_gate) uses the same zone, so the UI and
 * the trigger agree on the day someone turns 16.
 */
export const AGE_TIME_ZONE = 'Europe/Zurich';

/**
 * Completed years between a `date_of_birth` (`YYYY-MM-DD`) and today's
 * calendar date in Europe/Zurich.
 *
 * The DOB is parsed field by field, never through `new Date('YYYY-MM-DD')`
 * (UTC midnight, Critical Rule #19). "Today" is read in Europe/Zurich, not the
 * server's or the browser's zone, so a birthday flips at Swiss midnight
 * everywhere, exactly like the database trigger.
 *
 * @param dateOfBirth - `YYYY-MM-DD` (a time suffix is ignored)
 * @param now - injectable clock for tests (default: `new Date()`)
 * @returns completed years, or null for an unparseable date
 *
 * @example
 * // 2026-08-14 in Zurich
 * ageInCompletedYears('2010-08-14') // 16 (the birthday itself counts)
 * ageInCompletedYears('2010-08-15') // 15
 */
export function ageInCompletedYears(
  dateOfBirth: string,
  now: Date = new Date(),
): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(dateOfBirth.trim());

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: AGE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: 'year' | 'month' | 'day') =>
    Number(parts.find((p) => p.type === type)?.value);

  const todayYear = part('year');
  const todayMonth = part('month');
  const todayDay = part('day');

  let age = todayYear - year;

  if (todayMonth < month || (todayMonth === month && todayDay < day)) {
    age -= 1;
  }

  return age;
}
